import { Injectable, inject } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { SortByCriteria } from '@cds/query-objects';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { EventFactory } from '@frontend/sports/grid/core/feature/factories';
import { GridGrouping, GridModel, GridSorting, SubscriptionTopic } from '@frontend/sports/grid/core/feature/model';
import { GridActions, IGridRootState } from '@frontend/sports/grid/core/feature/store';
import { StatisticsConfigService } from '@frontend/sports/statistics/core/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { toInteger } from 'lodash-es';
import { Observable, concatMap, filter, map, of, switchMap, tap, withLatestFrom } from 'rxjs';

import { CouponApi } from '../cds/coupon-api.service';
import { SortingStateService } from '../event-list-core/services/sorting-state.service';
import { OptionGroupService } from '../option-group/option-group.service';

@Injectable({ providedIn: 'root' })
export class CouponGridEffects {
    private eventFactory = inject(EventFactory);
    private routeService = inject(RouterEventsService);
    private sortingStore = inject(SortingStateService);
    private couponService = inject(CouponApi);
    private actions$ = inject(Actions);
    private store = inject<Store<IGridRootState>>(Store);
    private statisticsConfigService = inject(StatisticsConfigService);
    private optionGroupService = inject(OptionGroupService);

    private tag: RouteTag;
    private coupon: number;
    private sport: number;

    private get couponRoute(): boolean {
        return !!this.coupon && this.tag === RouteTag.CouponDetails;
    }

    constructor() {
        this.routeService.currentActivationEnd.subscribe((route) => {
            if (route) {
                this.coupon = toInteger(route.snapshot.params.coupon);
                this.sport = toInteger(route.snapshot.params.sport);
                this.tag = route.snapshot.data.tag;
            }
        });
    }

    onSortingChange$ = createEffect(() => {
        return this.actions$.pipe(
            filter(() => this.couponRoute),
            ofType(GridActions.changeSorting),
            concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(map((state) => state.grid[action.payload.id]))))),
            switchMap(([action, grid]) => {
                const grouping = grid.sorting === GridSorting.Competition ? GridGrouping.League : GridGrouping.Date;
                const sorting = grid.sorting === GridSorting.Competition ? SortByCriteria.Tags : SortByCriteria.StartDate;
                const statisticsModes = this.statisticsConfigService.getStatisticsModesBySportId(this.sport);

                this.sortingStore.set('coupons', action.payload.sorting, this.sport);

                return this.couponService.getCoupon(this.sport.toString(), this.coupon, 0, 30, sorting, statisticsModes).pipe(
                    switchMap((page) =>
                        this.eventFactory.create(page?.fixturePage.fixtures, { marketGrouping: grid.topic !== SubscriptionTopic.Grid }),
                    ),
                    map((events) => {
                        if (events.length === 0) {
                            return [];
                        }

                        return [GridActions.setEvents({ payload: { id: action.payload.id, events, grouping } })];
                    }),
                );
            }),
            concatMap((actions) => actions),
        );
    });

    onAddCouponEvents = createEffect(
        () => {
            const couponEffect = this;

            return this.actions$.pipe(
                ofType(GridActions.addCouponEvents),
                filter(() => this.couponRoute),
                concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(map((state) => state.grid[action.payload.id]))))),
                concatMap(([action, grid]) => {
                    return this.getEvents({ fixtures: action.payload.events }, grid).pipe(
                        tap((events) =>
                            couponEffect.store.dispatch(
                                GridActions.addEvents({
                                    payload: {
                                        id: grid?.id,
                                        events,
                                    },
                                }),
                            ),
                        ),
                        map((events) => {
                            return { id: grid?.id, action, events, grid };
                        }),
                    );
                }),
                concatMap(function* (payload) {
                    const periodGroups = couponEffect.optionGroupService.getPeriodGroupsFromEvents(payload.action.payload.couponType, payload.events);
                    yield couponEffect.getEvents({ fixtures: payload.action.payload.events }, payload.grid).pipe(
                        map(() =>
                            couponEffect.store.dispatch(
                                GridActions.addGroups({
                                    payload: {
                                        id: payload?.id,
                                        groups: periodGroups,
                                    },
                                }),
                            ),
                        ),
                    );
                }),
            );
        },
        { dispatch: false },
    );

    private getEvents(source: { fixtures: Fixture[] } | undefined, grid: GridModel): Observable<EventModel[]> {
        return this.eventFactory.create(source?.fixtures, { marketGrouping: grid?.topic !== SubscriptionTopic.Grid });
    }
}
